import React, { useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  Snackbar,
  Alert,
  SvgIcon
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  Instagram,
  Twitter,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';
import axios from '../utils/axiosConfig';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/v2/contact', formData);
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Message sent successfully! We will get back to you soon.',
          severity: 'success'
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSnackbar({
        open: true,
        message: 'Error sending message. Please try again later.',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const contactInfo = [
    {
      icon: <Phone />,
      title: 'Phone',
      content: '+1 (516) 414-9528',
    },
    {
      icon: <Email />,
      title: 'Email',
      content: 'solo@narrowgatepath.com',
    },
    {
      icon: <LocationOn />,
      title: 'Location',
      content: '74 Hauppauge Rd, Commack, NY 11725',
    },
  ];
  const TikTokIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.298-.002.595.042.88.13V9.4a6.13 6.13 0 0 0-1-.08A6.25 6.25 0 0 0 3 15.57a6.25 6.25 0 0 0 10.95 4.14.25.25 0 0 0 .05-.08 6.22 6.22 0 0 0 .58-2.62v-7.5a8.51 8.51 0 0 0 5.19 1.74V8.11a5.08 5.08 0 0 1-2.93-.87 4.9 4.9 0 0 1-.98-.82" />
    </SvgIcon>
  );
  
  const socialMedia = [
    { icon: <TikTokIcon />, name: 'TikTok', link: 'https://tiktok.com/@middiesgetbiddies' },
    { icon: <Instagram />, name: 'Instagram', link: 'https://www.instagram.com/narrowgate.basketball/' },
    { icon: <Twitter />, name: 'Twitter', link: '#' },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 10, mb: 5 }}>
      <Typography variant="h2" gutterBottom align="center">
        Contact Us
      </Typography>
      <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 5 }}>
        Get in touch with us for any questions or inquiries
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom>
              Send us a Message
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    multiline
                    rows={4}
                    label="Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{ mt: 2 }}
                  >
                    Send Message
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 4, height: '100%' }}>
            <Typography variant="h5" gutterBottom>
              Contact Information
            </Typography>
            <Box sx={{ mt: 4 }}>
              {contactInfo.map((info, index) => (
                <Box key={index} sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                  <IconButton sx={{ mr: 2, backgroundColor: 'primary.main', color: 'white' }}>
                    {info.icon}
                  </IconButton>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {info.title}
                    </Typography>
                    <Typography color="text.secondary">{info.content}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              Follow Us
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {socialMedia.map((social, index) => (
                <IconButton
                  key={index}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': { backgroundColor: 'primary.dark' },
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 6 }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          icon={snackbar.severity === 'success' ? <CheckCircle /> : <ErrorIcon />}
          sx={{
            width: '100%',
            maxWidth: '500px',
            fontSize: '1.1rem',
            alignItems: 'center',
            '& .MuiAlert-icon': {
              fontSize: '2rem'
            },
            boxShadow: 3,
            '& .MuiAlert-message': {
              padding: '10px 0'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Contact;
